import React, { useState, useEffect, useRef } from 'react';

import ActionButton from '../elements/ActionButton/ActionButton';
import useContactForm from '../../hooks/useContactForm';
import { useAdobeDataLayer, PageNames } from '../../hooks/useAdobeDataLayer';
import ContactFormFields, { ContactSuccessIcon } from './ContactFormFields';

import './ContactModal.css';

const ContactModal = ({ isOpen, onClose }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [render, setRender] = useState(isOpen);
  const modalRef = useRef(null);
  const successTimerRef = useRef(null);
  const { sendPageView } = useAdobeDataLayer();

  const {
    formData,
    errors,
    isSubmitting,
    submitError,
    showSuccessMessage,
    handleChange,
    handleSubmit,
    resetForm,
  } = useContactForm(() => {
    successTimerRef.current = setTimeout(() => {
      onClose();
      resetForm();
    }, 6000);
  }, PageNames.ContactModal);

  const handleClose = () => {
    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
      successTimerRef.current = null;
    }
    resetForm();
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      sendPageView(PageNames.ContactModal);
    }
  }, [isOpen, sendPageView]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setRender(true);
      setIsClosing(false);
    } else if (render) {
      setIsClosing(true);
      const timer = setTimeout(() => {
        setRender(false);
        setIsClosing(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, render]);

  useEffect(() => {
    if (!render || !isOpen || !modalRef.current) return;

    const focusable = [
      ...modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      ),
    ];
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    first?.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleClose();
        return;
      }
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [render, isOpen]);

  if (!render) return null;

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('contact-modal-overlay')) {
      handleClose();
    }
  };

  return (
    <div
      className={`contact-modal-overlay ${isClosing ? 'closing' : ''}`}
      onClick={handleOverlayClick}
    >
      <div
        ref={modalRef}
        className={`contact-modal-content ${isClosing ? 'closing' : ''}`}
        role='dialog'
        aria-modal='true'
        aria-labelledby='contact-modal-title'
      >
        <button
          className='contact-modal-close'
          onClick={handleClose}
          aria-label='Close contact modal'
        >
          <svg
            width='32'
            height='32'
            viewBox='0 0 32 32'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M15.9974 15.9974L25.3307 25.3307M15.9974 15.9974L6.66406 6.66406M15.9974 15.9974L6.66406 25.3307M15.9974 15.9974L25.3307 6.66406'
              stroke='white'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </button>

        <div className='contact-modal-header'>
          <h2 id='contact-modal-title' className='contact-modal-title'>
            LET’S CONNECT!
          </h2>
          <p className='contact-modal-subtitle'>Reach out to us, and we'll schedule a call soon.</p>
        </div>

        <form onSubmit={handleSubmit} className='contact-modal-form'>
          <ContactFormFields
            formData={formData}
            errors={errors}
            handleChange={handleChange}
            idPrefix='modal'
          />

          <div className='contact-modal-submit'>
            {submitError && (
              <div className='contact-modal-error-message' role='alert'>
                <span>{submitError}</span>
              </div>
            )}
            {showSuccessMessage && (
              <div className='contact-modal-success-message' role='status'>
                <ContactSuccessIcon />
                <span>We have received your message and will respond soon!</span>
              </div>
            )}
            <div className='form-submit-row'>
              <ActionButton
                onClick={handleSubmit}
                text={isSubmitting ? 'Sending...' : 'Contact Us'}
                disabled={isSubmitting || showSuccessMessage}
                clickTrackValue='Contact Modal|Contact Us|Internal|'
                fullWidth
              />
              <ActionButton
                href='https://www.marriott.com/about/privacy.mi'
                target='_blank'
                text='Privacy Center'
                className='dark'
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactModal;
